import React from 'react';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import logoImg from "../../assets/97af715ab6a3414a5586695e213784f67e1e0ed5.png";

export const ContactForm = () => {
  const [formData, setFormData] = React.useState({
    company: '',
    designation: '',
    workEmail: '',
    mobileNumber: '',
    projectGoals: '',
  });
  const [errors, setErrors] = React.useState({
    company: '',
    designation: '',
    workEmail: '',
    mobileNumber: '',
    projectGoals: '',
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const inputClassName = (hasError: boolean) =>
    `w-full px-5 py-3 rounded-xl border transition-all outline-none ${
      hasError
        ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/10'
        : 'border-gray-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10'
    }`;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const nextValue =
      name === 'mobileNumber' ? value.replace(/[^0-9\s+-]/g, '') : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const nextErrors = {
      company: '',
      designation: '',
      workEmail: '',
      mobileNumber: '',
      projectGoals: '',
    };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[0-9\s-]{7,20}$/;

    if (formData.company.trim().length > 120) {
      nextErrors.company = 'Company name must be 120 characters or less.';
    }

    if (formData.designation.trim().length > 80) {
      nextErrors.designation = 'Designation must be 80 characters or less.';
    }

    if (!emailRegex.test(formData.workEmail.trim())) {
      nextErrors.workEmail = 'Please enter a valid work email.';
    }

    if (!phoneRegex.test(formData.mobileNumber.trim())) {
      nextErrors.mobileNumber = 'Please enter a valid mobile number.';
    }

    if (formData.projectGoals.trim().length > 1000) {
      nextErrors.projectGoals = 'Project goals must be 1000 characters or less.';
    }

    return nextErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }

    const nextErrors = validateForm();
    const hasErrors = Object.values(nextErrors).some((value) => value.length > 0);
    if (hasErrors) {
      setErrors(nextErrors);
      toast.error('Please fix the highlighted fields and try again.');
      return;
    }

    setIsSubmitting(true);
    const apiBaseUrl = 'https://ecoclubsbackend.aicte-india.org';
    const payload = {
      company: formData.company.trim(),
      designation: formData.designation.trim(),
      workEmail: formData.workEmail.trim(),
      mobileNumber: formData.mobileNumber.trim(),
      projectGoals: formData.projectGoals.trim(),
    };

    try {
      const response = await fetch(`${apiBaseUrl}/api/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        if (data?.errors) {
          setErrors((prev) => ({ ...prev, ...data.errors }));
        }
        throw new Error('Request failed');
      }

      toast.success('Request submitted! Our team will contact you shortly.');
      setFormData({
        company: '',
        designation: '',
        workEmail: '',
        mobileNumber: '',
        projectGoals: '',
      });
      setErrors({
        company: '',
        designation: '',
        workEmail: '',
        mobileNumber: '',
        projectGoals: '',
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-2 bg-gray-50 scroll-mt-28">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto bg-white rounded-[3rem] shadow-2xl shadow-indigo-100 overflow-hidden flex flex-col md:flex-row">
          {/* Left Side - Info */}
          <div className="md:w-1/3 bg-gradient-to-br from-indigo-600 to-indigo-900 p-12 text-white">
            {/* <div className="mb-8 w-fit rounded-2xl bg-white/10 p-3"> */}
              <img src={logoImg} alt="Anuvadini Logo" className="h-24 rounded-2xl mb-1" />
            {/* </div> */}
            <h2 className="text-4xl font-bold mb-6">Build with Anuvadini</h2>
            <p className="text-indigo-100 mb-12 leading-relaxed text-justify-inter-word">
              Share your requirements and our team will respond with a tailored roadmap for your multilingual AI journey.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 bg-indigo-300 rounded-full" />
                </div>
                <div>
                  <h4 className="font-bold">Enterprise Ready</h4>
                  <p className="text-sm text-indigo-200 text-justify-inter-word">Scale with confidence on our secure infra.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 bg-indigo-300 rounded-full" />
                </div>
                <div>
                  <h4 className="font-bold">24/7 Support</h4>
                  <p className="text-sm text-indigo-200 text-justify-inter-word">Global assistance for your integration needs.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="md:w-2/3 p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Company</label>
                  <input 
                    type="text" 
                    name="company"
                    placeholder="Enter company name"
                    value={formData.company}
                    onChange={handleChange}
                    maxLength={120}
                    className={inputClassName(Boolean(errors.company))}
                    aria-invalid={Boolean(errors.company)}
                    aria-describedby={errors.company ? 'company-error' : undefined}
                  />
                  {errors.company && (
                    <p id="company-error" className="text-xs text-red-500 mt-2">
                      {errors.company}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Designation</label>
                  <input
                    type="text"
                    name="designation"
                    placeholder="Enter designation"
                    value={formData.designation}
                    onChange={handleChange}
                    maxLength={80}
                    className={inputClassName(Boolean(errors.designation))}
                    aria-invalid={Boolean(errors.designation)}
                    aria-describedby={errors.designation ? 'designation-error' : undefined}
                  />
                  {errors.designation && (
                    <p id="designation-error" className="text-xs text-red-500 mt-2">
                      {errors.designation}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Work Email</label>
                  <input 
                    type="email" 
                    name="workEmail"
                    placeholder="Enter work email"
                    value={formData.workEmail}
                    onChange={handleChange}
                    maxLength={120}
                    className={inputClassName(Boolean(errors.workEmail))}
                    required
                    aria-invalid={Boolean(errors.workEmail)}
                    aria-describedby={errors.workEmail ? 'workEmail-error' : undefined}
                  />
                  {errors.workEmail && (
                    <p id="workEmail-error" className="text-xs text-red-500 mt-2">
                      {errors.workEmail}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Mobile Number</label>
                  <input
                    type="tel"
                    name="mobileNumber"
                    placeholder="Enter mobile number"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                    inputMode="numeric"
                    pattern="^\+?[0-9\s-]{7,20}$"
                    className={inputClassName(Boolean(errors.mobileNumber))}
                    required
                    aria-invalid={Boolean(errors.mobileNumber)}
                    aria-describedby={errors.mobileNumber ? 'mobileNumber-error' : undefined}
                  />
                  {errors.mobileNumber && (
                    <p id="mobileNumber-error" className="text-xs text-red-500 mt-2">
                      {errors.mobileNumber}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Project Goals</label>
                <textarea 
                  rows={4}
                  name="projectGoals"
                  placeholder="Tell us about your project requirements..."
                  value={formData.projectGoals}
                  onChange={handleChange}
                  maxLength={1000}
                  className={`${inputClassName(Boolean(errors.projectGoals))} resize-none`}
                  aria-invalid={Boolean(errors.projectGoals)}
                  aria-describedby={errors.projectGoals ? 'projectGoals-error' : undefined}
                />
                {errors.projectGoals && (
                  <p id="projectGoals-error" className="text-xs text-red-500 mt-2">
                    {errors.projectGoals}
                  </p>
                )}
              </div>

              <button
                className="w-full md:w-auto px-10 py-4 bg-green-400 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-green-500 transition-all shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'} <Send size={18} />
              </button>
              
              <p className="text-xs text-gray-500 mt-4 text-justify-inter-word">
                By submitting, you agree to our privacy policy and terms of service.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};